/**
 * Users.tsx — Operator lifecycle management page (Phase 9 D-09-18, SC-1/2/3).
 *
 * Features:
 *   - Active operators table (Display Name / Email / Role / Joined / Remove)
 *   - Pending invitations table (Email / Role / Invited / Revoke)
 *   - Invite operator Dialog (email + role)
 *   - Remove / Revoke confirmation Dialogs (alertdialog; Cancel autoFocus)
 *   - Last-admin guard: Remove disabled/greyed when only one admin
 *
 * Security: Remove and Revoke are destructive; confirmations required.
 * All mutations use operatorsApi (Plans 03/04 routes).
 */

import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi, operatorsApi, ApiRequestError } from '../lib/api-client.js';
import type { OperatorRow } from '@crm/types';
import {
  Button,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Dialog,
  Input,
  Select,
  SelectOption,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  useToast,
} from '@crm/ui';
import { formatBerlin } from '../lib/time.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function roleBadge(role: 'admin' | 'chatter') {
  return role === 'admin' ? (
    <Badge variant="success">Admin</Badge>
  ) : (
    <Badge variant="outline">Chatter</Badge>
  );
}

// ---------------------------------------------------------------------------
// Invite Dialog
// ---------------------------------------------------------------------------

interface InviteDialogProps {
  open: boolean;
  onClose: () => void;
  onInvite: (email: string, role: 'admin' | 'chatter') => void;
  isPending: boolean;
}

function InviteDialog({ open, onClose, onInvite, isPending }: InviteDialogProps) {
  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState<'admin' | 'chatter'>('chatter');
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    onInvite(email.trim(), role);
  };

  // Reset on open
  React.useEffect(() => {
    if (open) {
      setEmail('');
      setRole('chatter');
      setError(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} title="Invite Operator">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-4">
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isPending}
        />
        <Select
          label="Role"
          value={role}
          onChange={(e) => setRole(e.target.value as 'admin' | 'chatter')}
          disabled={isPending}
        >
          <SelectOption value="chatter">Chatter</SelectOption>
          <SelectOption value="admin">Admin</SelectOption>
        </Select>

        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button type="submit" size="sm" loading={isPending}>
            Send Invite
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Remove Confirmation Dialog
// ---------------------------------------------------------------------------

interface RemoveDialogProps {
  operator: OperatorRow;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}

function RemoveDialog({ operator, onClose, onConfirm, isPending }: RemoveDialogProps) {
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Remove ${operator.displayName || operator.email}?`}
    >
      <div className="flex flex-col gap-4 p-4" role="alertdialog" aria-modal="true">
        <p className="text-sm text-text">
          This will immediately end their sessions and remove all account
          assignments. This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          {/* Cancel autoFocused per alertdialog pattern */}
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            loading={isPending}
            onClick={onConfirm}
            aria-label={`Confirm remove ${operator.displayName || operator.email}`}
          >
            Remove
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Revoke Confirmation Dialog
// ---------------------------------------------------------------------------

interface RevokeDialogProps {
  invitation: OperatorRow;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}

function RevokeDialog({ invitation, onClose, onConfirm, isPending }: RevokeDialogProps) {
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Revoke invitation for ${invitation.email}?`}
    >
      <div className="flex flex-col gap-4 p-4" role="alertdialog" aria-modal="true">
        <p className="text-sm text-text">
          The invite link will no longer work.
        </p>
        <div className="flex justify-end gap-2">
          {/* Cancel autoFocused per alertdialog pattern */}
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            loading={isPending}
            onClick={onConfirm}
            aria-label={`Confirm revoke invitation for ${invitation.email}`}
          >
            Revoke
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function Users() {
  const { addToast } = useToast();
  const qc = useQueryClient();

  // Current user (for self-guard + last-admin guard)
  const meQ = useQuery({ queryKey: ['me'], queryFn: () => authApi.me() });

  const operatorsQ = useQuery({
    queryKey: ['operators'],
    queryFn: () => operatorsApi.list(),
  });

  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [removeTarget, setRemoveTarget] = React.useState<OperatorRow | null>(null);
  const [revokeTarget, setRevokeTarget] = React.useState<OperatorRow | null>(null);

  // Derived: active + pending lists
  const active = (operatorsQ.data ?? []).filter((r) => r.status === 'active');
  const pending = (operatorsQ.data ?? []).filter((r) => r.status === 'pending');

  // Last-admin guard: is there only one active admin?
  const adminCount = active.filter((r) => r.role === 'admin').length;
  const isLastAdmin = adminCount <= 1;

  // Invite mutation
  const inviteMut = useMutation({
    mutationFn: ({ email, role }: { email: string; role: 'admin' | 'chatter' }) =>
      operatorsApi.invite({ email, role }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['operators'] });
      addToast(`Invitation sent to ${vars.email}`, 'success');
      setInviteOpen(false);
    },
    onError: (err) => {
      const msg = err instanceof ApiRequestError ? err.message : 'Invite failed';
      addToast(msg, 'danger');
    },
  });

  // Remove mutation
  const removeMut = useMutation({
    mutationFn: (id: string) => operatorsApi.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['operators'] });
      const name = removeTarget?.displayName || removeTarget?.email || 'Operator';
      addToast(`${name} has been removed`, 'success');
      setRemoveTarget(null);
    },
    onError: (err) => {
      const msg = err instanceof ApiRequestError ? err.message : 'Remove failed';
      addToast(msg, 'danger');
      setRemoveTarget(null);
    },
  });

  // Revoke mutation
  const revokeMut = useMutation({
    mutationFn: (id: string) => operatorsApi.revokeInvitation(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['operators'] });
      addToast('Invitation revoked', 'success');
      setRevokeTarget(null);
    },
    onError: (err) => {
      const msg = err instanceof ApiRequestError ? err.message : 'Revoke failed';
      addToast(msg, 'danger');
      setRevokeTarget(null);
    },
  });

  const myId = meQ.data?.id;

  return (
    <div className="max-w-5xl mx-auto flex flex-col gap-6">
      {/* Page heading + Invite CTA */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-text">Users</h1>
        <Button onClick={() => setInviteOpen(true)}>Send Invitation</Button>
      </div>

      {operatorsQ.isLoading && (
        <div className="p-4 text-sm text-text-muted">Loading…</div>
      )}

      {operatorsQ.error && (
        <div className="p-4 text-sm text-danger">
          {(operatorsQ.error as Error).message}
        </div>
      )}

      {/* Active Operators */}
      {!operatorsQ.isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2 className="text-xs font-medium text-text-muted uppercase tracking-wide">
                Active Operators ({active.length})
              </h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {active.length === 0 ? null : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Display Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {active.map((op) => {
                    const isSelf = op.id === myId;
                    const removeDisabled = isSelf || (isLastAdmin && op.role === 'admin');
                    return (
                      <TableRow key={op.id} className="h-12">
                        <TableCell>
                          <span className="font-medium text-text truncate max-w-[200px] block">
                            {op.displayName || '—'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-text-muted text-sm">{op.email}</span>
                        </TableCell>
                        <TableCell>{roleBadge(op.role)}</TableCell>
                        <TableCell>
                          <span className="text-text-subtle text-xs">
                            {formatBerlin(op.createdAt)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="danger"
                            size="sm"
                            className="min-h-[44px]"
                            disabled={removeDisabled}
                            onClick={() => !removeDisabled && setRemoveTarget(op)}
                            aria-label={`Remove ${op.displayName || op.email}`}
                            title={removeDisabled ? 'Cannot remove the last active admin account.' : undefined}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pending Invitations */}
      {!operatorsQ.isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2 className="text-xs font-medium text-text-muted uppercase tracking-wide">
                Pending Invitations ({pending.length})
              </h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {pending.length === 0 ? (
              <p className="text-sm text-text-muted py-4 px-4">No pending invitations.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Invited</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pending.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell>
                        <span className="text-text font-medium">{inv.email}</span>
                      </TableCell>
                      <TableCell>{roleBadge(inv.role)}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning/20 text-warning border border-warning/30">
                          Pending
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-text-subtle text-xs">
                          {formatBerlin(inv.createdAt)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-h-[44px]"
                          onClick={() => setRevokeTarget(inv)}
                          aria-label={`Revoke invitation for ${inv.email}`}
                        >
                          Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invite Dialog */}
      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvite={(email, role) => inviteMut.mutate({ email, role })}
        isPending={inviteMut.isPending}
      />

      {/* Remove Confirmation Dialog */}
      {removeTarget && (
        <RemoveDialog
          operator={removeTarget}
          onClose={() => setRemoveTarget(null)}
          onConfirm={() => removeMut.mutate(removeTarget.id)}
          isPending={removeMut.isPending}
        />
      )}

      {/* Revoke Confirmation Dialog */}
      {revokeTarget && (
        <RevokeDialog
          invitation={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onConfirm={() => revokeMut.mutate(revokeTarget.id)}
          isPending={revokeMut.isPending}
        />
      )}
    </div>
  );
}
